import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  signUp as cognitoSignUp,
  confirmSignUp as cognitoConfirmSignUp,
  fetchUserAttributes as cognitoFetchUserAttributes,
  fetchAuthSession as cognitoFetchAuthSession,
  resetPassword as cognitoResetPassword,
  confirmResetPassword as cognitoConfirmResetPassword
} from '@aws-amplify/auth';

// Configure Amplify
import { Amplify } from '@aws-amplify/core';
import * as awsExports from '../aws-exports';

Amplify.configure(awsExports.amplifyConfig);

interface User {
  username: string;
  email: string;
  sub: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string | undefined }>;
  signUp: (email: string, password: string, name: string) => Promise<{ success: boolean; error?: string | undefined }>;
  confirmSignUp: (email: string, code: string) => Promise<{ success: boolean; error?: string | undefined }>;
  signOut: () => Promise<{ success: boolean; error?: string | undefined }>;
  forgotPassword: (email: string) => Promise<{ success: boolean; error?: string | undefined }>;
  forgotPasswordSubmit: (email: string, code: string, newPassword: string) => Promise<{ success: boolean; error?: string | undefined }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await cognitoFetchAuthSession();
      const userAttributes = await cognitoFetchUserAttributes();
      
      const userData = {
        email: userAttributes.email || '',
        name: userAttributes.name || userAttributes.email?.split('@')[0] || '',
        picture: userAttributes.picture || `https://ui-avatars.com/api/?name=${userAttributes.name || userAttributes.email?.split('@')[0] || ''}&background=random`,
        sub: userAttributes.sub || '',
        username: userAttributes.username || userAttributes.email || ''
      };
      
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Error checking auth state:', error);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      // Debug: Log parameters before calling cognitoSignIn
      console.log('Calling cognitoSignIn with:', { email, passwordLength: password?.length });
      
      const cognitoUser = await cognitoSignIn({
        username: email,
        password: password
      });
      
      // Debug: Log successful sign in
      console.log('Sign in response:', cognitoUser);
      
      // Check if sign-in requires confirmation
      if (cognitoUser.nextStep && cognitoUser.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        console.log('Account needs confirmation');
        setIsLoading(false);
        return { 
          success: false, 
          error: 'Please confirm your email first. Check your inbox for a verification code.',
          needsConfirmation: true
        };
      }
      
      // Get user attributes - handle potential identity pool issues
      let userAttributes;
      try {
        userAttributes = await cognitoFetchUserAttributes();
        console.log('User attributes retrieved:', userAttributes);
      } catch (attrError) {
        console.warn('Could not fetch user attributes, using basic info:', attrError);
        // Fall back to basic user info without attributes
        userAttributes = {
          email: email,
          name: email.split('@')[0],
          sub: email,
          username: email
        };
      }
      
      const userData = {
        email: userAttributes.email || email,
        name: userAttributes.name || email.split('@')[0],
        picture: userAttributes.picture || `https://ui-avatars.com/api/?name=${userAttributes.name || email.split('@')[0]}&background=random`,
        sub: userAttributes.sub || email,
        username: userAttributes.username || email
      };
      
      setUser(userData);
      setIsAuthenticated(true);
      setIsLoading(false);
      return { success: true, error: undefined };
    } catch (error: any) {
      setIsLoading(false);
      let errorMessage = 'An error occurred during sign in';
      
      if (error.code === 'UserNotFoundException') {
        errorMessage = 'User does not exist';
      } else if (error.code === 'NotAuthorizedException') {
        errorMessage = 'Incorrect username or password';
      } else if (error.code === 'UserNotConfirmedException') {
        errorMessage = 'Please confirm your account before signing in';
      } else if (error.code === 'TooManyRequestsException') {
        errorMessage = 'Too many failed attempts. Please try again later';
      } else if (error.code === 'UserAlreadyAuthenticatedException') {
        // User is already authenticated, just get their info
        await checkAuthState();
        return { success: true, error: undefined };
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    setIsLoading(true);
    try {
      const result = await cognitoSignUp({
        username: email,
        password
      });
      
      setIsLoading(false);
      return { 
        success: true,
        error: result.isSignUpComplete ? undefined : 'Please check your email to confirm your account'
      };
    } catch (error: any) {
      setIsLoading(false);
      let errorMessage = 'An error occurred during sign up';
      
      if (error.code === 'UsernameExistsException') {
        errorMessage = 'An account with this email already exists';
      } else if (error.code === 'InvalidParameterException') {
        errorMessage = 'Invalid parameters provided';
      } else if (error.code === 'NotAuthorizedException' && error.message.includes('SECRET_HASH')) {
        errorMessage = 'Authentication configuration error. Please contact support.';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const confirmSignUp = async (email: string, code: string) => {
    setIsLoading(true);
    try {
      // Debug: Log parameters
      console.log('Confirm sign up - email:', email, 'code:', code);
      
      if (!email || !email.trim()) {
        throw new Error('Email is required for confirmation');
      }
      
      if (!code || !code.trim()) {
        throw new Error('Verification code is required');
      }
      
      // Use correct AWS Amplify v6 parameter format
      await cognitoConfirmSignUp({
        username: email.trim(),
        confirmationCode: code.trim()
      });
      
      setIsLoading(false);
      return { success: true };
    } catch (error: any) {
      setIsLoading(false);
      let errorMessage = 'An error occurred during confirmation';
      
      if (error.code === 'CodeMismatchException') {
        errorMessage = 'Invalid confirmation code';
      } else if (error.code === 'ExpiredCodeException') {
        errorMessage = 'Confirmation code has expired';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const signOut = async () => {
    try {
      await cognitoSignOut();
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      return { success: true };
    } catch (error: any) {
      setIsLoading(false);
      return { success: false, error: 'An error occurred during sign out' };
    }
  };

  const forgotPassword = async (email: string) => {
    setIsLoading(true);
    try {
      await cognitoResetPassword({ username: email });
      setIsLoading(false);
      return { success: true };
    } catch (error: any) {
      setIsLoading(false);
      let errorMessage = 'An error occurred';
      
      if (error.code === 'UserNotFoundException') {
        errorMessage = 'User not found';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const forgotPasswordSubmit = async (email: string, code: string, newPassword: string) => {
    setIsLoading(true);
    try {
      await cognitoConfirmResetPassword({ username: email, confirmationCode: code, newPassword });
      setIsLoading(false);
      return { success: true };
    } catch (error: any) {
      setIsLoading(false);
      let errorMessage = 'An error occurred';
      
      if (error.code === 'CodeMismatchException') {
        errorMessage = 'Invalid confirmation code';
      } else if (error.code === 'ExpiredCodeException') {
        errorMessage = 'Confirmation code has expired';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    signIn,
    signUp,
    confirmSignUp,
    signOut,
    forgotPassword,
    forgotPasswordSubmit
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
